import { Injectable } from '@angular/core';
import { HttpStatusCode } from '@angular/common/http';
import { ExaminationService } from 'src/app/examination/examination.service';
import { MediaAccessService } from './media-access.service';
import { IDBPDatabase, openDB } from 'idb';

@Injectable({
  providedIn: 'root',
})
export class VideoUploadService {
  isApiRequestPending = false;
  uploadedFilesSet = new Set<string>();
  uploadedScreenFilesSet = new Set<string>();
  isRecordedChunkMissed = false;
  dbName = 'WebcamChunksDB';
  webcamStore = 'webcamChunks';
  screenStore = 'screenChunks';
  submitStore = 'submitAllData';
  dbPromise: Promise<IDBPDatabase>;

  constructor(
    private _examinationService: ExaminationService,
    private mediaAccessService: MediaAccessService
  ) {
    setTimeout(() => {
      this.dbPromise = openDB(this.dbName, 3, {
        upgrade(db) {
          if (!db.objectStoreNames.contains('webcamChunks')) {
            db.createObjectStore('webcamChunks');
          }
          if (!db.objectStoreNames.contains('screenChunks')) {
            db.createObjectStore('screenChunks');
          }
          if (!db.objectStoreNames.contains('submitAllData')) {
            db.createObjectStore('submitAllData');
          }
        },
      });
    }, 100);
  }

  /**
   * New chunk files are named "<prefix>-<index>-<timestamp>.webm", where
   * <index> is the durable, write-time sequence assigned by the main process
   * (electron.main.js). Deriving the upload sequence from the filename keeps
   * it correct across app restarts and partial-drain retries, unlike an
   * in-memory counter that resets on every relaunch. Returns null for files
   * written before this naming scheme existed.
   */
  private parseDurableSequence(filePath: string): number | null {
    const match = /-(\d+)-\d+\.webm$/.exec(filePath);
    return match ? parseInt(match[1], 10) : null;
  }

  async checkAndUploadSavedChunks(onChunkUploaded?: () => void): Promise<void> {
    try {
      const videoFiles = await window.electron.ipcRenderer.invoke(
        'get-saved-videos'
      );

      if (!videoFiles?.length) {
        console.log('No saved videos found.');
        return;
      }

      if (this.mediaAccessService.isNetworkConnectionEnabled()) {
        // Best-effort numbering for any pre-existing file with no embedded
        // index — placed after every file that does have one so it can't
        // collide with a real sequence.
        let legacyFallbackSequence =
          Math.max(
            -1,
            ...videoFiles.map((f: string) => this.parseDurableSequence(f) ?? -1)
          ) + 1;

        for (const filePath of videoFiles) {
          if (this.uploadedFilesSet.has(filePath)) {
            console.log(`Skipping already uploaded file: ${filePath}`);
            continue;
          }

          const sequence =
            this.parseDurableSequence(filePath) ?? legacyFallbackSequence++;

          const videoBlob = await this.readFileAsBlob(filePath);

          // Create a download link for testing
          // this.downloadFile(videoBlob, 'recorded-video.webm');

          const uploaded = await this.uploadVideoToServer(
            videoBlob,
            'WEB_FEED',
            sequence
          );

          // 👇 Per-chunk progress callback
          onChunkUploaded?.();

          // Only drop the file once the server actually has it — a failed
          // upload stays on disk and is retried (with the same sequence) on
          // the next drain pass instead of being silently lost.
          if (uploaded) {
            this.uploadedFilesSet.add(filePath);
            window.electron.ipcRenderer.invoke('delete-video-chunk', filePath);
          }
        }
      }
    } catch (error) {
      onChunkUploaded?.();
      console.error('Error fetching saved videos:', error);
    }
  }

  //     downloadFile(blob: Blob, filename: string) {
  //     const url = window.URL.createObjectURL(blob);
  //     const a = document.createElement('a');
  //     a.style.display = 'none';
  //     a.href = url;
  //     a.download = filename;
  //     document.body.appendChild(a);
  //     a.click();
  //     window.URL.revokeObjectURL(url);
  //     document.body.removeChild(a);
  //   }

  async uploadVideoToServer(
    videoBlob: Blob,
    chunkType: any,
    sequence: number
  ): Promise<boolean> {
    const formData = new FormData();
    const fileData = new File([videoBlob], 'file', { type: 'video/webm' });
    let fileSize = videoBlob.size;
    let contentType = fileData.type;
    formData.append('file', fileData);

    if (!this.shouldUploadVideo(formData)) return false;

    try {
      this.isApiRequestPending = true;
      const response = await this._examinationService
        .saveChunkVideos(fileData, sequence, chunkType, fileSize, contentType)
        .toPromise();

      return (
        !!response &&
        (response.statusCode === HttpStatusCode.Ok || response.status == 200)
      );
    } catch (error) {
      if (!this.isRecordedChunkMissed) {
        console.error('API upload error:', error);
        this.isRecordedChunkMissed = true;
      }
      return false;
    } finally {
      this.isApiRequestPending = false;
    }
  }

  shouldUploadVideo(formData: FormData): boolean {
    return formData.has('file');
  }

  async readFileAsBlob(filePath: string): Promise<Blob> {
    try {
      const buffer = await window.electron.ipcRenderer.invoke(
        'read-video-file',
        filePath
      );
      return new Blob([buffer], { type: 'video/webm' });
    } catch (error) {
      console.error('Error reading file as blob:', error);
      throw error;
    }
  }

  async getPendingVideoChunkCount(): Promise<number> {
    try {
      const videoFiles = await window.electron.ipcRenderer.invoke(
        'get-saved-videos'
      );

      if (!Array.isArray(videoFiles)) {
        console.warn('get-saved-videos did not return an array:', videoFiles);
        return 0;
      }

      const pendingChunks = videoFiles.filter(
        (filePath) => !this.uploadedFilesSet.has(filePath)
      );
      return pendingChunks.length;
    } catch (error) {
      console.error('Error fetching saved videos:', error);
      return 0;
    }
  }

  async checkAndUploadScreenSavedChunks(onChunkUploaded?: () => void): Promise<void> {
    try {
      const screenVideoFiles = await window.electron.ipcRenderer.invoke(
        'get-screen-saved-videos'
      );

      if (!screenVideoFiles.length) {
        console.log('No saved screen share videos found.');
        return;
      }

      const filesToUpload = screenVideoFiles.filter(
        (file) => !this.uploadedScreenFilesSet.has(file.path)
      );

      if (!filesToUpload.length) return;

      // Best-effort numbering for any pre-existing file with no embedded
      // index — placed after every file that does have one so it can't
      // collide with a real sequence.
      let legacyFallbackSequence =
        Math.max(
          -1,
          ...screenVideoFiles.map(
            (f: any) => this.parseDurableSequence(f.path) ?? -1
          )
        ) + 1;

      for (const file of filesToUpload) {
        const filePath = file.path;

        const sequence =
          this.parseDurableSequence(filePath) ?? legacyFallbackSequence++;

        const videoBlob = await this.readFileAsBlob(filePath);
        const uploaded = await this.uploadScreenVideoToServer(
          videoBlob,
          'SCREEN_SHARE',
          sequence
        );

        // 👇 Per-chunk progress callback
        onChunkUploaded?.();

        // Only drop the file once the server actually has it — a failed
        // upload stays on disk and is retried (with the same sequence) on
        // the next drain pass instead of being silently lost.
        if (uploaded) {
          this.uploadedScreenFilesSet.add(filePath);
          window.electron.ipcRenderer.invoke('delete-video-chunk', filePath);
        }
      }
    } catch (error) {
      console.error('Error fetching saved screen share videos:', error);
      onChunkUploaded?.();
    }
  }

  async uploadScreenVideoToServer(
    videoBlob: Blob,
    chunkType: any,
    sequence: number
  ): Promise<boolean> {
    const formData = new FormData();
    const fileData = new File([videoBlob], 'file', { type: 'video/webm' });
    let fileSize = videoBlob.size;
    let contentType = fileData.type;
    formData.append('file', fileData);

    if (!this.shouldUploadVideo(formData)) return false;

    try {
      this.isApiRequestPending = true;
      const response = await this._examinationService
        .saveChunkVideos(fileData, sequence, chunkType, fileSize, contentType)
        .toPromise();

      return (
        !!response &&
        (response.statusCode == HttpStatusCode.Ok || response.status == 200)
      );
    } catch (error) {
      if (!this.isRecordedChunkMissed) {
        console.error('API error:', error);
        this.isRecordedChunkMissed = true;
      }
      return false;
    } finally {
      this.isApiRequestPending = false;
    }
  }

  async getPendingScreenVideoChunkCount(): Promise<number> {
    try {
      const screenVideoFiles = await window.electron.ipcRenderer.invoke(
        'get-screen-saved-videos'
      );
      const pendingChunks = screenVideoFiles.filter(
        (file) => !this.uploadedScreenFilesSet.has(file.path)
      );
      return pendingChunks.length;
    } catch (error) {
      console.error('Error fetching saved screen share videos:', error);
      return 0;
    }
  }

  // Webcam chunk methods
  async saveWebcamChunk(sequence: number, chunk: Blob): Promise<void> {
    const db = await this.dbPromise;
    await db.put(this.webcamStore, chunk, sequence);
  }

  async getWebcamChunk(sequence: number): Promise<Blob | undefined> {
    const db = await this.dbPromise;
    return db.get(this.webcamStore, sequence);
  }

  async deleteWebcamChunk(sequence: number): Promise<void> {
    const db = await this.dbPromise;
    await db.delete(this.webcamStore, sequence);
  }

  // Screen chunk methods
  async saveScreenChunk(sequence: number, chunk: Blob): Promise<void> {
    const db = await this.dbPromise;
    await db.put(this.screenStore, chunk, sequence);
  }

  async getScreenChunk(sequence: number): Promise<Blob | undefined> {
    const db = await this.dbPromise;
    return db.get(this.screenStore, sequence);
  }

  async deleteScreenChunk(sequence: number): Promise<void> {
    const db = await this.dbPromise;
    await db.delete(this.screenStore, sequence);
  }

  async getAllChunks(
    storeName: 'webcamChunks' | 'screenChunks'
  ): Promise<{ key: number; chunk: Blob }[]> {
    const db = await this.dbPromise;
    const tx = db.transaction(storeName, 'readonly');
    const store = tx.objectStore(storeName);

    const keys = await store.getAllKeys();
    const chunks = await store.getAll();

    return keys.map((key, i) => ({ key: key as number, chunk: chunks[i] }));
  }

  async deleteChunk(
    sequence: number,
    storeName: 'webcamChunks' | 'screenChunks'
  ): Promise<void> {
    const db = await this.dbPromise;
    await db?.delete(storeName, sequence);
  }

  async setItem(key: string, value: any): Promise<void> {
    const db = await this.dbPromise;
    await db?.put('submitAllData', value, key);
  }

  async getItem<T>(key: string): Promise<T | null> {
    const db = await this.dbPromise;
    return (await db?.get('submitAllData', key)) ?? null;
  }

  async deleteItem(key: string): Promise<void> {
    const db = await this.dbPromise;
    await db?.delete('submitAllData', key);
  }

  async clearAll(): Promise<void> {
    const db = await this.dbPromise;
    await db?.clear('submitAllData');
  }
}
